import {
  ArrowTopRightOnSquareIcon,
  BoltSlashIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  SquaresPlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { FormikValues } from 'formik'
import { get, isEmpty, isEqual, merge } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router'
import { Link, useHistory } from 'react-router-dom'

import { getCurrentOrganizationId } from '@/components/App/selectors'
import ReadOnlyBanner, { BannerTypes } from '@/components/Banner/Banner'
import Loading from '@/components/Loading'
import Modal from '@/components/Modal'
import { useRoles } from '@/components/RolesContainer'
import SharedWith from '@/components/Segments/Builder/SharedWith'
import Banner from '@/components/TailwindUI/Banner'
import Breadcrumbs from '@/components/TailwindUI/Breadcrumbs'
import Button from '@/components/TailwindUI/Button'
import Menu, { MenuItem } from '@/components/TailwindUI/Menu'
import Skeleton from '@/components/TailwindUI/Skeleton'
import StatusIndicator from '@/components/TailwindUI/StatusIndicator'
import useFeatures from '@/components/useFeatures'
import { BUILDER_ORG_ADMIN_ACCESS, WORKSHOP_AGENTS } from '@/constants/featureToggles'
import useMessage from '@/hooks/useMessage'
import { useNotification } from '@/hooks/useNotification'
import ShareOwnershipModal from '@/pages/SpaceAppBuilder/components/ShareOwnershipModal/ShareOwnershipModal'
import { WorkgridDatasourceForm } from '@/pages/SpaceAppBuilder/components/WorkgridDatasourceForm'
import { extractUiSchema } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Form'
import { getAgentDescription, getAgentInstructions } from '@/pages/SpaceAppBuilder/utilities/agentHelpers'
import {
  App,
  AppInstanceDataSource,
  AppInstanceDataSourceItems,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import isDatasourceOwner from '@/pages/SpaceAppBuilder/utilities/isDatasourceOwner'
import {
  dataSourceV3CatalogQueryOptions,
  dataSourceV3MembersQueryOptions,
  dataSourceV3QueryOptions,
  getDataSourceAppsQueryOptions,
  getFetchAppQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import { getAccessToken } from '@/store/contextSubscriber'
import { useAppSelector } from '@/store/hooks'
import { getCurrentUser } from '@/store/selectors'

import RenameDataSourceModal from './components/RenameDataSourceModal'

export default function WorkgridDataSourceDetailsPage() {
  const { spaceId, datasourceId, datasourceInstanceId } = useParams<{
    spaceId: string
    datasourceId: string
    datasourceInstanceId: string
  }>()
  const queryClient = useQueryClient()
  const history = useHistory()
  const roles = useRoles()
  const features = useFeatures()
  const previousSpaceId = useRef<string>(spaceId)

  const { notify } = useNotification()

  const [isPendingUserAuthConnection, setIsPendingUserAuthConnection] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isEditingConfig, setIsEditingConfig] = useState(false)
  const [isShowingConnectedNotification, setIsShowingConnectedNotification] = useState(false)
  const [isLoadingDataSourceApps, setIsLoadingDataSourceApps] = useState(false)
  const [isRenameDataSourceModalOpen, setIsRenameDataSourceModalOpen] = useState(false)
  const [isSharingDatasource, setIsSharingDatasource] = useState(false)
  const [dataSourceApps, setDataSourceApps] = useState<Array<AppInstanceDataSource>>([])
  const [error, setError] = useState<any>()

  const organizationId = useAppSelector(getCurrentOrganizationId)
  const currentUser = useAppSelector(getCurrentUser)

  const hasOrgAdminRole = roles.hasOrganizationAdminRole(organizationId)

  const dataSourceInstanceQuery = useQuery<WorkgridDatasource>({
    ...dataSourceV3QueryOptions({ spaceId, datasourceId, instanceId: datasourceInstanceId }),

    async onSuccess() {
      setIsLoadingDataSourceApps(true)
      const dataSourceAppsQueryResult = await queryClient.fetchQuery<AppInstanceDataSourceItems>(
        getDataSourceAppsQueryOptions({ spaceId, dataSourceId: datasourceInstanceId })
      )
      setDataSourceApps(dataSourceAppsQueryResult.items)
      setIsLoadingDataSourceApps(false)
    }
  })

  const workgridDatasourceQueryResult = useQuery<WorkgridCatalogDatasource>(
    dataSourceV3CatalogQueryOptions({ spaceId, datasourceId })
  )

  const dataSourceMutation = useMutation({
    onSuccess: async () => {
      // Reload the Datasource
      await dataSourceInstanceQuery.refetch()
    }
  })

  const dataSourceDeleteMutation = useMutation({})

  useMessage('workgrid-auth', (send, payload) => {
    if (payload.success) {
      setIsShowingConnectedNotification(true)
      setError(null)
      setIsEditingConfig(false)
      history.push(`/space/${spaceId}/appbuilder/datasources`, {
        name: dataSourceInstance?.name,
        vendorName: workgridDataSource?.name
      })
    } else {
      setError({
        message: 'Failed to authenticate. Please check your credentials or configuration',
        subMessage: (payload.error as any).data
      })
      setIsShowingConnectedNotification(false)
      setError(null)
      setIsEditingConfig(false)
    }
  })

  useEffect(() => {
    if (previousSpaceId.current !== spaceId) {
      history.push(`/space/${spaceId}/appbuilder/datasources`)
    }
  }, [spaceId, history])

  const workgridDataSource = workgridDatasourceQueryResult.data
  const dataSourceInstance = dataSourceInstanceQuery.data
  const dataSourceConfig = dataSourceInstance?.configuration
  const isLoadingDataSourceInstance = dataSourceInstanceQuery.isLoading

  const [originalSharedList, setOriginalSharedList] = useState<string[] | undefined>()
  const [unaccessibleAppCount, setUnaccessibleAppCount] = useState<number>(0)

  const [connectionName, setConnectionName] = useState(dataSourceInstance?.name || null)

  const appDetailsQueries = useQueries<App[]>(
    dataSourceApps?.map(appDataSource => ({
      ...getFetchAppQueryOptions({ spaceId, appId: appDataSource.applicationId }),
      staleTime: Number.POSITIVE_INFINITY,
      retry: (failureCount, error: unknown) => {
        if (get(error, 'status') === 403 || get(error, 'data') === 'You are not permitted to access this resource') {
          return false
        } else {
          return true
        }
      }
    })) ?? []
  )

  const mutateMembersList = useMutation({
    onSuccess: () => {
      dataSourceInstanceQuery.refetch()
    }
  })

  useEffect(() => {
    const errorCount = appDetailsQueries?.filter(app => get(app, 'error.status') === 403).length || 0
    setUnaccessibleAppCount(errorCount)
    // eslint-disable-next-line @tanstack/query/no-unstable-deps
  }, [appDetailsQueries])

  const hasConnectedApps = dataSourceInstanceQuery.status === 'success' && dataSourceApps.length > 0

  const hasConfigurationFields = dataSourceConfig

  const readOnly = !isDatasourceOwner(dataSourceInstance, currentUser?.id)
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  async function handleSubmit(values: any) {
    const configurationValues = values.formData
    setIsEditingConfig(false)
    const isOauth = configurationValues.auth === 'oauth2'
    if (isOauth) {
      setIsPendingUserAuthConnection(true)
      configurationValues.dataSourceId = dataSourceInstance?.id
      configurationValues.name = connectionName || dataSourceInstance?.name

      const configuration = encodeURIComponent(JSON.stringify(configurationValues))
      const param = `spaceId=${spaceId}&datasourceId=${dataSourceInstance?.datasourceId}&datasourceInstanceId=${dataSourceInstance?.id}&name=${dataSourceInstance?.name}&configuration=${configuration}`
      const encodedParam = encodeURIComponent(param)
      const authorizeWindow = window.open(
        `/console/datasource/callback?${encodedParam}#token=${getAccessToken()}`,
        '_blank'
      )
      authorizeWindow?.focus()
    } else {
      dataSourceMutation.mutate(
        // @ts-expect-error
        [
          dataSourceV3QueryOptions({ spaceId, datasourceId, instanceId: datasourceInstanceId }).queryKey[0],
          {
            headers: { 'content-type': 'application/json' },
            method: 'PATCH',
            body: JSON.stringify({
              configuration: configurationValues,
              name: connectionName || dataSourceInstance?.name
            })
          }
        ],
        {
          onError: err => {
            setError((err as any).message)
          },
          onSuccess: () => {
            setIsShowingConnectedNotification(true)
            setError(null)
            setIsEditingConfig(false)
          }
        }
      )
    }
  }

  function saveName(values: FormikValues) {
    dataSourceMutation.mutate(
      // @ts-expect-error
      [
        dataSourceV3QueryOptions({ spaceId, datasourceId, instanceId: datasourceInstanceId }).queryKey[0],
        {
          headers: { 'content-type': 'application/json' },
          method: 'PATCH',
          body: JSON.stringify({ name: values.name })
        }
      ]
    )
    setConnectionName(values.name)
    setIsRenameDataSourceModalOpen(false)
  }

  function deleteDataSource() {
    dataSourceDeleteMutation.mutate(
      // @ts-expect-error
      [
        dataSourceV3QueryOptions({ spaceId, datasourceId, instanceId: datasourceInstanceId }).queryKey[0],
        { method: 'DELETE', headers: { 'content-type': 'application/json' } }
      ],
      {
        onSuccess: () => {
          queryClient.refetchQueries(dataSourceV3QueryOptions({ spaceId }))
          navToDataSources()
        }
      }
    )
  }

  function navToDataSources() {
    history.push(`/space/${spaceId}/appbuilder/datasources`, {
      isFromDelete: true,
      name: dataSourceInstance?.name,
      vendorName: workgridDataSource?.name
    })
  }

  const saveMembersList = async (memberIds: string[]) => {
    try {
      // @ts-expect-error
      await mutateMembersList.mutateAsync([
        dataSourceV3MembersQueryOptions({ spaceId, datasourceId, instanceId: datasourceInstanceId }).queryKey[0],
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ owners: memberIds || [] })
        }
      ])
      if (!isEqual(originalSharedList, memberIds)) {
        notify({
          variant: 'success',
          title: 'Datasource Owners Updated',
          body: (
            <div className="text-sm text-zinc-500">
              <span>Changes have been successfully saved.</span>
            </div>
          )
        })
      }
      setOriginalSharedList(memberIds)
    } catch {
      notify({
        variant: 'error',
        title: 'Datasource Owners Failed to updated',
        body: (
          <div className="text-sm text-zinc-500">
            <span>Changes have </span>
            <span className="font-bold">not</span>
            <span> been saved.</span>
          </div>
        )
      })
    }
  }

  useEffect(() => {
    if (dataSourceInstanceQuery.data) {
      setOriginalSharedList(dataSourceInstanceQuery.data.owners)
    }
  }, [dataSourceInstanceQuery.data])

  const appNames =
    appDetailsQueries?.map(app => {
      const appData = app?.data as App
      if (appData) return { name: appData?.name, id: appData?.id }
    }) ?? []

  const isPreLoadingApps =
    appDetailsQueries.length === 0 &&
    (isLoadingDataSourceApps || isLoadingDataSourceInstance || dataSourceInstanceQuery.isRefetching)

  const buildUiSchema = (schema: Record<string, any> | undefined) => {
    if (schema) {
      return (
        merge(extractUiSchema(schema), {
          callbackUrl: {
            'ui:widget': 'CallBackUrl'
          }
        }) || {}
      )
    }
    return {}
  }

  return (
    <div className="fade-in w-full">
      {dataSourceInstance && (
        <ShareOwnershipModal
          isOpen={isSharingDatasource}
          descriptor="data source instance"
          title="Data source owners"
          onClose={() => setIsSharingDatasource(false)}
          onSubmit={userIds => {
            setIsSharingDatasource(false)
            saveMembersList(userIds)
          }}
          sharedUserList={dataSourceInstance.owners || []}
          readOnly={readOnly && !hasOrgAdminRole}
        />
      )}
      {isPendingUserAuthConnection ? (
        <div className="mr-auto ml-auto flex max-h-[500px] flex-col justify-center">
          <Loading
            isLoading={isPendingUserAuthConnection}
            loadingMessage={`Redirecting to ${workgridDataSource?.name}...`}
          />
          <div className="ml-auto mr-auto -mt-40 max-w-[200px]">
            <Button
              color="secondary"
              type="button"
              onClick={() => {
                setIsPendingUserAuthConnection(false)
                setIsShowingConnectedNotification(false)
                setError(null)
                setIsEditingConfig(false)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Modal size="sm" isOpen={isConfirmModalOpen}>
            <div className="p-6 sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3 className="text-base font-semibold leading-6 text-zinc-900">Delete data source</h3>
                {hasConnectedApps ? (
                  <div className="mt-2">
                    <p className="text-sm text-zinc-500">
                      Unable to delete data source as it is used by the following {agentsFlagOn ? 'agent' : 'app'}(s).
                      If you want to delete this data source, first go to the {agentsFlagOn ? 'agent' : 'app'}(s) listed
                      below and delete the data source connection.
                    </p>
                    <ul className="mt-3 list-inside list-disc pl-4 text-sm text-zinc-500">
                      {!isEmpty(appNames) &&
                        appNames?.map(app => (
                          <li className="mb-1" key={app?.id}>
                            <Link to={`/space/${spaceId}/appbuilder/${app?.id}`}>{app?.name}</Link>
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : dataSourceMutation.isLoading ? (
                  <div className="mt-2">
                    <p className="text-sm text-zinc-500">
                      Deleting data source... You will be redirected to the data sources page upon completion.
                    </p>
                  </div>
                ) : (
                  <div className="mt-2">
                    <p className="text-sm text-zinc-500">
                      Are you sure you want to delete this data source? All of your configuration data will be
                      permanently removed. This action cannot be undone.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {!dataSourceMutation.isLoading && (
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:bg-red-300 sm:ml-3 sm:w-auto"
                  onClick={() => deleteDataSource()}
                  disabled={hasConnectedApps || dataSourceDeleteMutation.isLoading}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 sm:mt-0 sm:w-auto"
                  onClick={() => setIsConfirmModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </Modal>
          <RenameDataSourceModal
            isOpen={isRenameDataSourceModalOpen}
            formValues={{ name: connectionName || dataSourceInstance?.name }}
            onClose={() => setIsRenameDataSourceModalOpen(false)}
            onSubmit={saveName}
          />
          <div className="w-full">
            <div className="transition-all">
              <div className="flex items-center justify-between px-12 py-3">
                <Breadcrumbs
                  breadcrumbs={[
                    { label: 'Data sources', path: `/space/${spaceId}/appbuilder/datasources` },
                    { label: connectionName || dataSourceInstance?.name || 'Loading...' }
                  ]}
                />

                <div className="flex items-center">
                  <div className="pl-4 pr-4">
                    {features[BUILDER_ORG_ADMIN_ACCESS] && (
                      <Button
                        title="Data source owners"
                        onClick={() => setIsSharingDatasource(true)}
                        className="group flex h-8 w-7 items-center rounded-full border border-transparent bg-zinc-100 hover:border-blue-700 hover:bg-blue-100"
                      >
                        <SharedWith
                          sharedWith={dataSourceInstance?.owners || []}
                          isLoading={isLoadingDataSourceInstance}
                          className="w-fit"
                          isOverflowHidden={false}
                        />
                        <div className="pl-1">
                          <ChevronDownIcon className="text-zinc-950 h-3 w-3 group-hover:text-blue-700" />
                        </div>
                      </Button>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="group flex h-8 w-8 justify-center rounded-full border border-transparent bg-zinc-100 hover:border-blue-700 hover:bg-blue-100">
                      <Menu
                        title="More"
                        icon={<EllipsisVerticalIcon className="h-7 w-7 group-hover:stroke-blue-700" />}
                      >
                        <div className="py-1">
                          <MenuItem onClick={() => setIsRenameDataSourceModalOpen(true)}>
                            <PencilSquareIcon
                              className="mr-3 h-5 w-5 text-zinc-400 group-hover:text-zinc-500"
                              aria-hidden="true"
                            />
                            Rename
                          </MenuItem>
                          <MenuItem onClick={() => setIsConfirmModalOpen(true)}>
                            <TrashIcon
                              className="mr-3 h-5 w-5 text-zinc-400 group-hover:text-zinc-500"
                              aria-hidden="true"
                            />
                            Delete
                          </MenuItem>
                        </div>
                      </Menu>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="pt-1 transition-all sm:px-8 2xl:px-36">
              {dataSourceInstanceQuery.isLoading ? (
                <div className="flex items-center gap-6">
                  <div className="rounded-lg p-2">
                    <Skeleton className="h-20 w-20" />
                  </div>
                  <div>
                    <Skeleton className="h-6 w-48" />
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Skeleton className="relative inline-flex h-2 w-2 rounded-full" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex w-full items-center gap-6">
                  <div className="rounded-lg border bg-white p-2">
                    <img className="h-20 w-20 rounded-lg" src={workgridDataSource?.iconUrl} alt="data source icon" />
                  </div>
                  <div className="w-full">
                    <div className={`flex items-center ${readOnly ? 'justify-between' : ''}`}>
                      <button
                        onClick={() => setIsRenameDataSourceModalOpen(true)}
                        disabled={readOnly}
                        className="text-3xl font-bold hover:underline"
                      >
                        {connectionName || dataSourceInstance?.name}
                      </button>
                      {readOnly ? (
                        <div>
                          <ReadOnlyBanner
                            className={'cursor-pointer rounded-[20px]'}
                            type={BannerTypes.informational}
                            onClick={() => setIsSharingDatasource(true)}
                          >
                            <p className="px-2">
                              <strong>{'This datasource is read only because you do not own the datasource'}</strong>
                            </p>
                          </ReadOnlyBanner>
                        </div>
                      ) : (
                        <button onClick={() => setIsRenameDataSourceModalOpen(true)} className="ml-3 flex items-center">
                          <PencilSquareIcon className="h-5 w-5 stroke-zinc-400 stroke-2 hover:stroke-zinc-500" />
                        </button>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 text-zinc-500">
                      {isLoadingDataSourceInstance ? (
                        <StatusIndicator label="Connecting..." ping />
                      ) : dataSourceMutation.isLoading ? (
                        <StatusIndicator label="Loading..." ping />
                      ) : dataSourceInstance?.status === 'connected' ? (
                        <StatusIndicator label="Connected" variant="success" ping />
                      ) : dataSourceInstance?.status === 'disconnected' ? (
                        <StatusIndicator
                          className="text-red-600"
                          label="Re-authentication required"
                          variant="error"
                          ping
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="min-h-screen w-full bg-zinc-50">
              <div className="mt-6 flex min-h-screen justify-between gap-12 py-6 transition-all sm:px-8 2xl:px-36">
                <div className="w-full">
                  <div className="mb-4 flex items-baseline justify-between px-2">
                    <h2 className="text-xl font-bold">Configuration details</h2>
                    {!isEditingConfig && !isLoadingDataSourceInstance && !readOnly && (
                      <button
                        className="font-semibold text-blue-500 hover:text-blue-400"
                        onClick={() => {
                          setIsEditingConfig(true)
                          setIsShowingConnectedNotification(false)
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  <div className="rounded-xl bg-white py-6 px-6">
                    {isLoadingDataSourceInstance || dataSourceMutation.isLoading ? (
                      <div className="pt-3">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="mt-3 h-12 max-w-[600px]" />
                        <Skeleton className="mt-7 h-4 w-24" />
                        <Skeleton className="mt-3 h-12 max-w-[600px]" />
                        <Skeleton className="mt-7 h-4 w-24" />
                        <Skeleton className="mt-3 h-12 max-w-[600px]" />
                        <Skeleton className="mt-7 h-4 w-24" />
                      </div>
                    ) : hasConfigurationFields ? (
                      <>
                        {isShowingConnectedNotification ? (
                          <Banner variant="success" onClose={() => setIsShowingConnectedNotification(false)}>
                            Successfully connected
                          </Banner>
                        ) : error ? (
                          <Banner variant="error" onClose={() => setError(null)}>
                            <span className="font-semibold">Re-authentication Failed </span>
                            <span>{error}</span>
                          </Banner>
                        ) : dataSourceInstance?.status !== 'connected' && !isShowingConnectedNotification ? (
                          <Banner
                            variant="error"
                            customIcon={<BoltSlashIcon className="h-5 w-5 text-red-400" aria-hidden="true" />}
                          >
                            This data source has been disconnected. Re-authentication is required.
                          </Banner>
                        ) : null}

                        <WorkgridDatasourceForm
                          schema={workgridDataSource?.schema || {}}
                          uiSchema={buildUiSchema(workgridDataSource?.schema)}
                          initialFormData={dataSourceConfig}
                          disabled={!isEditingConfig}
                          onSubmit={handleSubmit}
                        >
                          <div className="flex justify-end gap-4 p-2">
                            <Button
                              color="secondary"
                              type="button"
                              onClick={() => setIsEditingConfig(false)}
                              hidden={!isEditingConfig}
                            >
                              {'Cancel'}
                            </Button>
                            <Button type="submit" color="primary" hidden={!isEditingConfig}>
                              {'Save'}
                            </Button>
                          </div>
                        </WorkgridDatasourceForm>
                      </>
                    ) : (
                      <div className="py-6 px-2 text-center text-sm text-zinc-500">
                        There are no fields to configure
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex w-full justify-end">
                  <div>
                    <div className="mb-4 flex items-baseline justify-between gap-3 px-2">
                      <h2 className="text-xl font-bold">{agentsFlagOn ? 'Agents' : 'Apps'} using this data source</h2>
                    </div>
                    <div className="flex flex-col gap-4 rounded-xl bg-white py-6 px-6">
                      {isPreLoadingApps ? (
                        <AppLoadingSkeleton />
                      ) : hasConnectedApps ? (
                        appDetailsQueries?.map((app, i) => {
                          const errorData: any = app.error
                          if (app.isError && errorData.status !== 403) {
                            return (
                              <div
                                key={`app-isError-${i}`}
                                className="flex w-[400px] rounded-lg border border-zinc-100 bg-white p-3"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex h-16 w-16 items-center justify-center rounded-md bg-zinc-100">
                                    <ExclamationTriangleIcon className="h-12 w-12 stroke-1 text-zinc-300" />
                                  </div>
                                  <div>
                                    <h1 className="text-md text-left font-bold">
                                      Unknown {agentsFlagOn ? 'Agent' : 'App'}
                                    </h1>
                                    <p className="text-left text-sm text-zinc-400">
                                      There was an error finding this {agentsFlagOn ? 'agent' : 'app'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          }

                          if (app.isLoading) {
                            return <AppLoadingSkeleton key={`app-isLoading-${i}`} />
                          }
                          const appData = app.data as App // useQueries typing does not seem to be following through to the results, so forced to cast
                          if (!isEmpty(appData)) {
                            return (
                              <Link key={appData.id} to={`/space/${spaceId}/appbuilder/${appData.id}`}>
                                <div className="fade-in flex w-[400px] rounded-lg border border-zinc-100 bg-white p-3 hover:cursor-pointer hover:ring-2">
                                  <div className="flex items-center gap-3">
                                    <img className="h-16 w-16 rounded-md" src={appData.iconUrl} alt="app icon" />
                                    <div>
                                      <h1 className="text-md text-left font-bold">{appData.name}</h1>
                                      <p className="text-left text-sm text-zinc-500 line-clamp-2">
                                        {agentsFlagOn ? getAgentDescription(appData) : appData?.description}
                                      </p>
                                      {agentsFlagOn && (
                                        <p className="text-left text-sm text-zinc-500 line-clamp-2">
                                          {getAgentInstructions(appData)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </Link>
                            )
                          }
                        })
                      ) : (
                        <div className="w-[400px] py-12">
                          <div className="flex items-center justify-center">
                            <SquaresPlusIcon className="h-16 w-16 stroke-1 text-zinc-400" />
                          </div>
                          <div className="flex items-center justify-center">
                            <div className="mt-4 px-2 text-sm text-zinc-500">
                              There are no {agentsFlagOn ? 'agent' : 'app'}s using this data source
                            </div>
                          </div>
                        </div>
                      )}
                      {unaccessibleAppCount > 0 && (
                        <div className="fade-in flex w-[400px] rounded-lg border border-zinc-100 bg-white p-3 hover:cursor-pointer hover:ring-2">
                          <div className="flex items-center gap-3">
                            <div className="text-md flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-md bg-zinc-100 font-bold">
                              +{unaccessibleAppCount}
                            </div>
                            <div>
                              <h1 className="text-md text-left font-bold">
                                {unaccessibleAppCount} {agentsFlagOn ? 'agent' : 'app'}s you do not own
                              </h1>
                              <p className="text-left text-sm text-zinc-500">
                                You do not have access to view {unaccessibleAppCount} additional{' '}
                                {agentsFlagOn ? 'agent' : 'app'}s using this datasource
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-col space-y-2">
                      {workgridDataSource?.supportUrl && (
                        <a
                          href={workgridDataSource?.supportUrl}
                          rel="noopener noreferrer"
                          target="_blank"
                          className="flex items-center font-medium text-blue-500 hover:underline dark:text-blue-500"
                        >
                          Credentials and Config Help
                          <ArrowTopRightOnSquareIcon
                            aria-hidden="true"
                            className="ml-1.5 mb-0.5 h-4 w-4 flex-shrink-0 text-blue-500 hover:underline dark:text-blue-500"
                          />
                        </a>
                      )}
                      {workgridDataSource?.vendorApiUrl && (
                        <a
                          href={workgridDataSource?.vendorApiUrl}
                          rel="noopener noreferrer"
                          target="_blank"
                          className="flex items-center font-medium text-blue-500 hover:underline dark:text-blue-500"
                        >
                          Vendor API Documentation
                          <ArrowTopRightOnSquareIcon
                            aria-hidden="true"
                            className="ml-1.5 mb-0.5 h-4 w-4 flex-shrink-0 text-blue-500 hover:underline dark:text-blue-500"
                          />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function AppLoadingSkeleton() {
  return (
    <div className="fade-in flex w-[400px] rounded-lg border border-zinc-100 bg-white p-3">
      <div className="flex items-center gap-3">
        <Skeleton className="h-16 w-16" />
        <div>
          <Skeleton className="h-4 w-44" />
          <Skeleton className="mt-2 h-3 w-72" />
          <Skeleton className="mt-2 h-3 w-32" />
        </div>
      </div>
    </div>
  )
}
